import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { ShieldCheck, FileDown, FileSpreadsheet, Pencil, History } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

// Scheda cliente: stato consensi privacy, date, storico modifiche, export PDF/Excel e modifica admin con nota obbligatoria
export default function CustomerConsentsPanel({ customer }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showHist, setShowHist] = useState(false);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/customers/${customer.id}/consents`).then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { load(); }, [customer.id]); // eslint-disable-line
  if (!data) return null;
  const isAdmin = user?.role === "admin";
  const canExport = isAdmin || (user?.permissions || []).includes("export_data");
  const keys = Object.keys(data.labels);
  const dl = async (fmt) => {
    try { await downloadFile(`/customers/${customer.id}/consents/export.${fmt}`, `consensi_${customer.card_number}.${fmt}`); toast.success(`Export ${fmt.toUpperCase()} scaricato`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async () => {
    if ((edit.note || "").trim().length < 3) return toast.error("Inserisci una nota (es. richiesta firmata in negozio)");
    setBusy(true);
    try {
      const payload = Object.fromEntries(keys.filter((k) => edit[k] !== data.flags[k]).map((k) => [k, edit[k]]));
      const { data: d } = await api.patch(`/customers/${customer.id}/consents`, { ...payload, note: edit.note });
      setData(d); setEdit(null); toast.success("Consensi aggiornati e registrati nello storico");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800 space-y-3" data-testid="customer-consents-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Consensi privacy</div>
        <div className="flex gap-1">
          {canExport && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => dl("pdf")} data-testid="consents-export-pdf"><FileDown className="h-3.5 w-3.5 mr-1" /> PDF</Button>}
          {canExport && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => dl("xlsx")} data-testid="consents-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>}
          {isAdmin && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-amber-300" onClick={() => setEdit({ ...data.flags, note: "" })} data-testid="consents-edit"><Pencil className="h-3.5 w-3.5 mr-1" /> Modifica</Button>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {keys.map((k) => (
          <div key={k} className="rounded-lg border border-zinc-800 p-2 flex items-center justify-between gap-2" data-testid={`consent-${k}`}>
            <span className="text-xs text-zinc-300">{data.labels[k].replace(" (necessario)", "")}</span>
            <span className={`pill text-[10px] ${data.flags[k] ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`} data-testid={`consent-${k}-value`}>{data.flags[k] ? "SÌ" : "NO"}</span>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-1">
        <span data-testid="consents-first-at">Primo rilascio: <b className="text-zinc-300">{fmt(data.first_at)}</b></span>
        <span data-testid="consents-updated-at">Ultima modifica: <b className="text-zinc-300">{fmt(data.updated_at)}</b></span>
        <span data-testid="consents-source">Origine: <b className="text-zinc-300">{data.source}</b></span>
      </div>
      <button type="button" className="text-xs text-amber-300 inline-flex items-center gap-1" onClick={() => setShowHist((v) => !v)} data-testid="consents-history-toggle">
        <History className="h-3.5 w-3.5" /> Storico modifiche ({data.history.length})
      </button>
      {showHist && (
        <ul className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="consents-history">
          {data.history.map((h, i) => (
            <li key={i} className="text-[11px] rounded-lg border border-zinc-800 p-2">
              <div className="flex justify-between gap-2 text-zinc-400"><span>{fmt(h.at)} · {h.source}{h.actor ? ` · ${h.actor}` : ""}</span></div>
              <div className="text-zinc-200 mt-0.5">{Object.entries(h.changes).map(([k, v]) => `${data.labels[k].replace(" (necessario)", "")}: ${v ? "SÌ" : "NO"}`).join(" · ") || "—"}</div>
              {h.note && <div className="text-zinc-500 italic mt-0.5">{h.note}</div>}
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="consents-edit-dialog">
          <DialogHeader><DialogTitle>Modifica consensi · {customer.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              {keys.map((k) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <Label className="text-sm text-zinc-300">{data.labels[k]}</Label>
                  <Switch checked={!!edit[k]} onCheckedChange={(v) => setEdit({ ...edit, [k]: v })} data-testid={`consents-edit-${k}`} />
                </div>
              ))}
              <div>
                <Label className="text-xs text-zinc-400">Nota obbligatoria (motivo / riferimento documento firmato)</Label>
                <Textarea value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" rows={2} placeholder="Es. modulo cartaceo firmato in negozio il 26/10" data-testid="consents-edit-note" />
              </div>
              <DialogFooter><Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-zinc-950" data-testid="consents-edit-save">Salva e registra</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
